import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SectionTitle from '@/components/base/SectionTitle';
import ShareModal from '@/pages/detail/components/ShareModal';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { getMovieById, getRelatedMovies, formatViews } from '@/utils/media';

export default function Detail() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageMeta(
    movie ? `${movie.title} - 影视详情 - 嫩草影院` : '影视资源未找到 - 嫩草影院',
    movie
      ? `查看《${movie.title}》的详细信息，包括剧情简介、主演和相关影视推荐。`
      : '很抱歉，你访问的影视资源不存在。',
    movie ? `${movie.title},${movie.genre.join(',')},嫩草影院` : '影视详情,嫩草影院',
  );

  if (!movie) {
    return (
      <div className="flex min-h-screen flex-col bg-background-950">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-900 text-foreground-500">
              <i className="ri-file-unknow-line text-4xl" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-50">
              影视资源未找到
            </h1>
            <p className="mt-2 max-w-md text-sm text-foreground-400">
              该影视可能已下架或链接有误，返回首页看看其他精彩内容吧。
            </p>
            <Link
              to="/"
              className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const fav = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 6);
  const categoryPath = movie.category === 'movie' ? '/category?type=movie' : '/category?type=tv';
  const categoryLabel = movie.category === 'movie' ? '电影' : '电视剧';

  const metaRows = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.actors.join(' / ') },
    { label: '类型', value: movie.genre.join(' / ') },
    { label: '年份', value: `${movie.year}年` },
    { label: '地区', value: movie.region },
    { label: '更新状态', value: movie.updateStatus },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto w-full px-4 py-8 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={categoryPath} className="transition-colors hover:text-primary-400">
              {categoryLabel}分类
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-300">{movie.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
            {/* 主封面 */}
            <div className="mx-auto w-full max-w-[300px] lg:mx-0">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-800 bg-background-900">
                <img
                  src={movie.cover}
                  alt={`${movie.title}封面`}
                  title={`${movie.title} - 嫩草影院`}
                  className="h-full w-full object-top"
                />
                <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-950">
                  {categoryLabel}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-3 rounded-lg border border-background-800 bg-background-900 p-4">
                <div className="flex-1 text-center">
                  <p className="flex items-center justify-center gap-1 font-heading text-xl font-extrabold text-accent-500">
                    <i className="ri-star-fill text-base" />
                    {movie.rating.toFixed(1)}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">综合评分</p>
                </div>
                <div className="h-10 w-px bg-background-800" />
                <div className="flex-1 text-center">
                  <p className="flex items-center justify-center gap-1 font-heading text-xl font-extrabold text-primary-400">
                    <i className="ri-fire-line text-base" />
                    {formatViews(movie.views)}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">播放热度</p>
                </div>
              </div>
            </div>

            {/* 信息区 */}
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-foreground-50 md:text-3xl">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {movie.genre.map((g) => (
                  <span
                    key={g}
                    className="rounded-full bg-secondary-500/15 px-3 py-1 text-xs font-medium text-secondary-300"
                  >
                    {g}
                  </span>
                ))}
                <span className="rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-400">
                  {movie.updateStatus}
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <span className="shrink-0 text-foreground-500">{row.label}</span>
                    <span className="text-foreground-200">{row.value}</span>
                  </div>
                ))}
              </div>

              {/* 观看入口占位 */}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="flex h-12 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
                >
                  <i className="ri-play-circle-line text-xl" />
                  观看入口（示例）
                </button>
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`flex h-12 items-center gap-2 whitespace-nowrap rounded-md border px-6 text-sm font-medium transition-colors ${
                    fav
                      ? 'border-accent-500 bg-accent-500/15 text-accent-500'
                      : 'border-background-700 text-foreground-200 hover:border-accent-500 hover:text-accent-500'
                  }`}
                >
                  <i className={fav ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
                  {fav ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex h-12 items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-6 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
              </div>

              <p className="mt-3 flex items-start gap-2 rounded-lg bg-background-900 px-4 py-3 text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mt-0.5 text-base text-primary-400" />
                本站为演示站点，不提供真实在线播放。观看入口仅为展示占位，实际观看请前往正规版权平台。
              </p>

              {/* 简介 */}
              <div className="mt-7">
                <h2 className="font-heading text-lg font-bold text-foreground-50">剧情简介</h2>
                <p className="mt-3 text-sm leading-7 text-foreground-300">{movie.summary}</p>
                <p className="mt-2 text-sm leading-7 text-foreground-400">
                  《{movie.title}》由{movie.director}执导，{movie.actors.join('、')}主演，是一部
                  {movie.genre.join('、')}题材的{categoryLabel}，{movie.year}年上映于{movie.region}，
                  目前状态为{movie.updateStatus}。喜欢此类题材的观众不妨一并看看下方的相关推荐。
                </p>
              </div>

              <div className="mt-6">
                <Link
                  to={categoryPath}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  <i className="ri-price-tag-3-line" />
                  所属分类：{categoryLabel}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

          {/* 相关推荐 */}
          <section className="mt-12">
            <SectionTitle
              title="相关推荐"
              subtitle="根据类型与主演为你精选"
              icon="ri-magic-line"
              moreTo={categoryPath}
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        </div>
      </main>

      {shareOpen && <ShareModal title={movie.title} onClose={() => setShareOpen(false)} />}

      <Footer />
    </div>
  );
}